/**
 * Translate raw backend/database error strings into friendly, user-facing
 * messages so that technical errors (SQL constraints, Python/DRF wrappers,
 * stack traces) are never shown to end users.
 *
 * This module has NO imports so it can be used from the low-level axios
 * interceptor without creating circular dependencies.
 */

const GENERIC_FALLBACK = "Something went wrong. Please try again.";

/** Markers that indicate a raw, technical error that should never reach a user. */
const TECHNICAL_MARKERS = [
  "duplicate key",
  "violates",
  "constraint",
  "errordetail",
  "traceback",
  "psycopg",
  "integrityerror",
  "programmingerror",
  "operationalerror",
  "detail:",
  "null value in column",
  "sqlstate",
  "nonetype",
  "stack trace",
  "<class",
  "exception",
  // Internal schema/grid plumbing — never meaningful to an end user.
  "schema",
  "grid_data",
  "column not found",
];

/** Unwrap DRF/Python `[ErrorDetail(string='...', code='...')]` wrappers to the inner message(s). */
function unwrapErrorDetail(raw: string): string {
  const re = /string=(['"])([\s\S]*?)\1,\s*code=/g;
  const parts: string[] = [];
  let match: RegExpExecArray | null;
  while ((match = re.exec(raw)) !== null) {
    if (match[2]) {
      parts.push(match[2]);
    }
  }
  return parts.length > 0 ? parts.join(" ") : raw;
}

/** True when a message still looks like a raw DB/stack error after unwrapping. */
export function looksTechnical(message: string): boolean {
  const lower = message.toLowerCase();
  return TECHNICAL_MARKERS.some(marker => lower.includes(marker));
}

/**
 * Map known raw error patterns to friendly text. Returns null when no known
 * pattern matches (so callers can decide how to handle the remainder).
 */
export function humanizeApiError(rawInput: string): string | null {
  if (!rawInput) {
    return null;
  }
  const raw = unwrapErrorDetail(rawInput);
  const lower = raw.toLowerCase();

  // Duplicate username — the backend stores username as "<email>_<TenantName>".
  if (
    lower.includes("duplicate key") &&
    lower.includes("users_user_username_key")
  ) {
    const match = raw.match(/key \(username\)=\(([^)]*)\)/i);
    const rawUsername = match?.[1] ?? "";
    // Strip the trailing "_<TenantName>" that the backend appends to the email.
    const email =
      rawUsername.includes("_") && rawUsername.includes("@")
        ? rawUsername.slice(0, rawUsername.lastIndexOf("_"))
        : rawUsername;
    return email
      ? `A user with the email "${email}" already exists.`
      : "A user with this email already exists.";
  }

  // Email already registered.
  if (
    lower.includes("email already exists") ||
    (lower.includes("email") && lower.includes("already exists")) ||
    (lower.includes("email") && lower.includes("already registered"))
  ) {
    return "This email is already registered. Please use a different email.";
  }

  // Phone / mobile already registered.
  if (
    (lower.includes("phone") || lower.includes("mobile")) &&
    (lower.includes("already exists") || lower.includes("already registered"))
  ) {
    return "This mobile number is already registered.";
  }

  // Missing password.
  if (
    lower.includes("password is required") ||
    (lower.includes("password") && lower.includes("required"))
  ) {
    return "A password is required.";
  }

  // Any other unique-constraint violation.
  if (
    lower.includes("duplicate key value violates unique constraint") ||
    (lower.includes("unique constraint") && lower.includes("already exists"))
  ) {
    return "This record already exists. Please use different details.";
  }

  // Foreign-key violation.
  if (
    lower.includes("foreign key constraint") ||
    lower.includes("violates foreign key")
  ) {
    return "This is linked to another record and can't be completed as requested.";
  }

  // Not-null / required column violation.
  if (
    lower.includes("null value in column") ||
    lower.includes("not-null constraint") ||
    lower.includes("violates not-null")
  ) {
    const column = raw.match(/null value in column "?([a-z0-9_]+)"?/i)?.[1];
    return column
      ? `A required field ("${column}") is missing.`
      : "A required field is missing.";
  }

  return null;
}

/**
 * Always return a safe, user-facing message:
 *  1. known patterns → friendly text,
 *  2. otherwise the unwrapped server message if it is human-readable,
 *  3. otherwise a generic fallback (so raw DB/stack errors are never shown).
 */
export function toUserFacingError(
  rawInput: unknown,
  fallback: string = GENERIC_FALLBACK
): string {
  const raw = typeof rawInput === "string" ? rawInput.trim() : "";
  if (!raw) {
    return fallback;
  }

  const known = humanizeApiError(raw);
  if (known) {
    return known;
  }

  const unwrapped = unwrapErrorDetail(raw).trim();
  if (!unwrapped || looksTechnical(unwrapped)) {
    return fallback;
  }

  return unwrapped;
}
